import { InlineEditorCancelButton, InlineEditorLogic, InlineEditorLogic_EditMode, InlineEditorLogic_ReadonlyMode, InlineEditorSaveButton, isEditMode, isReadonlyMode, useInlineEditor } from '@tableau/vizportalclient-common-components';
import { ButtonWidget, CompactInlineAlertWidget, InlineAlertType, TextFieldWidget } from '@tableau/widgets';
import React from 'react';
import { classes } from 'typestyle';
import _ from 'underscore';

import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { EditConnectionReactActionUtils } from '~src/connectivity/components/EditConnection/utils/EditConnectionActionUtils';
import { wideField } from '~src/settings/general-settings/styles/site-name-and-id/SiteNameAndIdStyles';
import { INPUT_WIDE_WIDTH } from '~src/settings/styles/CommonStyles';
import { cssClass } from '~src/style';

import { isValidDomain } from '../custom-domain/helpers/CustomDomainHelper';
import { EmailCustomizationSettingsSectionProps } from './EmailCustomizationSettingsSection';
import { ISiteSMTPSettings } from './serverApi/requests/SetSiteSMTPCredentialTagRequest';
import { ExternalSMTPConfiguration } from './serverApi/SiteSMTPCredential';

export const READONLY_CREDENTIALS_SECTION = 'readonly-credentials-section';
export const READONLY_SENT_FROM_TEST_ID = 'readonly-sent-from';
export const READONLY_DOMAIN_TEST_ID = 'readonly-domain';
export const READONLY_SERVER_ADDRESS_TEST_ID = 'readonly-server-address';
export const READONLY_PORT_TEST_ID = 'readonly-port';

export const EDIT_SERVER_ADDRESS_FIELD_TEST_ID = 'email-customization-smtp-address';
export const EDIT_PORT_FIELD_TEST_ID = 'email-customization-port';
export const EDIT_PASSWORD_FIELD_TEST_ID = 'email-customization-password';
export const EDIT_FRIENDLY_NAME_FIELD_TEST_ID = 'email-customization-friendly-name';
export const EDIT_DOMAIN_FIELD_TEST_ID = 'email-customization-domain';

export const CREDENTIALS_FORM_ERROR_TEST_ID = 'credentials-form-error';

export const CUSTOMIZATION_ON_RADIO_LABEL_TEST_ID = 'customization-on-label';
export const MAX_INPUT_LENGTH = 100;
const cssPrefix = 'EmailCustomization';

const emailCustomizationInputClass = cssClass(cssPrefix, 'credentials-form', {
  display: 'flex',
  flexDirection: 'column',
});

const credentialsFormButtonsClass = cssClass(cssPrefix, 'credentials-form-buttons',{
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
});

const readonlyCredentialsClass = cssClass(cssPrefix, 'readonly-field', {
  paddingTop: 4,
  paddingBottom: 4,
  $nest: {
    '& span': {
      paddingRight: 4,
    }
  }
});

export interface CredentialsEditorState {
  host: string;
  port: string;
  friendlyName: string;
  password: string;
  domain: string;
}

export interface EmailCustomizationOptionsProps extends EmailCustomizationSettingsSectionProps {
  useEmailCustomization: boolean;
}

export function EmailCustomizationOptions(props: EmailCustomizationOptionsProps) {
  const radioButtonLabel = <div data-tb-test-id={CUSTOMIZATION_ON_RADIO_LABEL_TEST_ID}>
    <span>{Messages.emailServerCustomizeOn()}</span>
  </div>;

  return <>
    {radioButtonLabel}
    <SMTPCredentialsSection {...props} />
  </>;
}

const emptyState: CredentialsEditorState = {
  host: '',
  port: '',
  password: '',
  friendlyName: '',
  domain: '',
};

export function SMTPCredentialsSection(props: EmailCustomizationOptionsProps) {
  const emailCustomizationSettings = props.settings.emailCustomizationSettings;

  const startingCreds = emailCustomizationSettings.isEmailCustomizationEnabled ? {
    host: emailCustomizationSettings.emailCustomizationSMTPSUrl,
    port: emailCustomizationSettings.emailCustomizationSMTPSPort + '',
    friendlyName: emailCustomizationSettings.emailCustomizationSenderFriendlyName,
    password: '',
    domain: emailCustomizationSettings.emailCustomizationDomain,
  } : emptyState;
  const [credentials, setCredentials] = React.useState<CredentialsEditorState>(startingCreds);
  const [firstOpen, setFirstOpen] = React.useState<boolean>(true);
  const [credentialsErrorMessage, setCredentialsErrorMessage] = React.useState<string>(undefined);

  const onSave = async (editorState: CredentialsEditorState): Promise<void> => {
    const emailCustomizationSettings: ISiteSMTPSettings = {
      custom_domain: editorState.domain.trim(),
      sender_friendly_name: editorState.friendlyName?.trim(),
      server_password: editorState.password,
      server_port: parseInt(editorState.port),
      server_url: editorState.host.trim(),
    };

    try {
      await ExternalSMTPConfiguration.setSiteSMTPCredential(emailCustomizationSettings, getSiteLuid());
      setCredentialsErrorMessage(undefined);
      setCredentials(editorState);
    } catch (e) {
      setCredentialsErrorMessage(Messages.emailServer_errorSubmittingCredentials());
      throw e; // need to reject to stay in edit mode
    }
  };

  const canSave = (editorSavedState: CredentialsEditorState) => {
    const { host, port, friendlyName, password, domain } = editorSavedState;

    const isValidHost = isHostValid(host);
    const isValidPort = isPortValid(port);
    const isValidFriendlyName = isFriendlyNameValid(friendlyName);
    const isValidPassword = isPasswordValid(password);
    const isValidDomain = isHostValid(domain);

    const isFormDirty = host !== editorStateOnEdit.host ||
      port !== editorStateOnEdit.port ||
      friendlyName !== editorStateOnEdit.friendlyName ||
      domain !== editorStateOnEdit.domain ||
      password !== editorStateOnEdit.password;

    const isRequiredFieldEmpty = _.isEmpty(host) || _.isEmpty(port) || _.isEmpty(password) || _.isEmpty(domain);

    return isValidHost && isValidPort && isValidFriendlyName && isValidPassword && isValidDomain
      && isFormDirty && !isRequiredFieldEmpty;
  };

  const editorStateOnEdit: CredentialsEditorState = {
    host: credentials.host,
    port: credentials.port,
    friendlyName: credentials.friendlyName,
    domain: credentials.domain,
    password: '',
  };

  const logic: InlineEditorLogic<CredentialsEditorState> = useInlineEditor<CredentialsEditorState>({
    editorStateWhenEnteringEditMode: editorStateOnEdit,
    canEdit: props.useEmailCustomization,
    onSave: onSave,
    canSaveFn: canSave,
  });

  React.useEffect(() => {
    if (!credentials.host && firstOpen && isReadonlyMode(logic) && props.useEmailCustomization) {
      logic.enterEditMode();
      setFirstOpen(false);
    }
  }, [credentials, firstOpen, logic, props.useEmailCustomization]);

  if (!props.useEmailCustomization) {
    return <></>;
  }

  if (isEditMode(logic)) {
    return <SMTPCredentialsEditMode editLogic={logic} credentialsErrorMessage={credentialsErrorMessage} />;
  } else {
    return <SMTPCredentialsReadonlyMode
      readonlyLogic={logic}
      credentials={credentials}
      setCredentials={setCredentials}
    />;
  }
}

const getSiteLuid = () => {
  return SessionSiteSelectors.siteLuid(getAppStore().getState());
};

interface SMTPCredentialsReadonlyModeProps {
  readonly readonlyLogic: InlineEditorLogic_ReadonlyMode;
  readonly credentials: CredentialsEditorState;
  readonly setCredentials: React.Dispatch<React.SetStateAction<CredentialsEditorState>>;
}

export function SMTPCredentialsReadonlyMode(props: SMTPCredentialsReadonlyModeProps) {
  const {readonlyLogic, credentials, setCredentials} = props;

  const removeCredentials = async () => {
    if(FeatureFlags.emailCustomizationSSDebug) {
      await ExternalSMTPConfiguration.deleteSiteSMTPCredential(getSiteLuid());
      setCredentials(emptyState);
    }
  };

  const hasCredentials = !!credentials.host;
  const credentialsSection = <div className={readonlyCredentialsClass} data-tb-test-id={READONLY_CREDENTIALS_SECTION}>
    <div data-tb-test-id={READONLY_SENT_FROM_TEST_ID}><span style={{fontWeight: 'bold'}}>{Messages.emailServer_emailSentFrom()}</span><span>{(credentials.friendlyName || 'no-reply') + ` <no-reply@${credentials.domain}>`}</span></div>
    <div data-tb-test-id={READONLY_DOMAIN_TEST_ID}><span style={{fontWeight: 'bold'}}>{Messages.emailServer_domain_colon()}</span><span>{credentials.domain}</span></div>
    <div data-tb-test-id={READONLY_SERVER_ADDRESS_TEST_ID}><span>{Messages.emailServer_smtpServerAddress_colon()}</span><span>{credentials.host}</span></div>
    <div data-tb-test-id={READONLY_PORT_TEST_ID}><span>{Messages.emailServer_portNumber_colon()}</span><span>{credentials.port}</span></div>
  </div>;

  return (
    <>
      {hasCredentials && credentialsSection}
      <div style={{ paddingTop: 4 }}>
        <ButtonWidget
          style={readonlyLogic.canEdit ? undefined : { visibility: 'hidden' }}
          disabled={!readonlyLogic.canEdit}
          handleClick={() => readonlyLogic.enterEditMode()}
          children={hasCredentials ? Messages.edit() : Messages.emailServer_addButton()}
          testId={'edit-server-credentials-button'}
        />
        {FeatureFlags.emailCustomizationSSDebug && <ButtonWidget onClick={removeCredentials}>{Messages.remove()}</ButtonWidget>}
      </div>
    </>
  );
}

interface SMTPCredentialsEditModeProps {
  readonly editLogic: InlineEditorLogic_EditMode<CredentialsEditorState>;
  readonly credentialsErrorMessage?: string;
}

export function SMTPCredentialsEditMode(props: SMTPCredentialsEditModeProps) {
  const { editLogic, credentialsErrorMessage } = props;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    if (e.key === 'Enter' && editLogic.canSaveThisEditorState) {
      editLogic.startSave();
    }
  };

  const truncateText = (input: string) => {
    return input.substring(0, MAX_INPUT_LENGTH);
  };

  return (
    <>
      <div style={{ width: INPUT_WIDE_WIDTH }}>
        <div style={{paddingBottom: 4}}>
          <div className={classes(emailCustomizationInputClass, wideField)}>
            <TextFieldWidget
              label={Messages.emailServer_smtpServerAddress()}
              onChange={e => editLogic.setEditorStateWithinEditMode({ host: truncateText(e.target.value) })}
              onClear={() => editLogic.setEditorStateWithinEditMode({ host: '' })}
              onKeyDown={handleKeyDown}
              required
              testId={EDIT_SERVER_ADDRESS_FIELD_TEST_ID}
              valid={!isHostValid(editLogic.editorState.host) ? false : undefined}
              value={editLogic.editorState.host}
            />
            <TextFieldWidget
              label={Messages.emailServer_portNumber()}
              onChange={e => editLogic.setEditorStateWithinEditMode({ port: truncateText(e.target.value) })}
              onClear={() => editLogic.setEditorStateWithinEditMode({ port: '' })}
              onKeyDown={handleKeyDown}
              placeholder={Messages.emailServer_portPlaceholder()}
              required
              testId={EDIT_PORT_FIELD_TEST_ID}
              valid={!isPortValid(editLogic.editorState.port) ? false : undefined}
              value={editLogic.editorState.port}
            />
            <TextFieldWidget
              label={Messages.password()}
              onChange={e => editLogic.setEditorStateWithinEditMode({ password: truncateText(e.target.value) })}
              onClear={() => editLogic.setEditorStateWithinEditMode({ password: '' })}
              onKeyDown={handleKeyDown}
              required
              testId={EDIT_PASSWORD_FIELD_TEST_ID}
              type='password'
              valid={!isPasswordValid(editLogic.editorState.password) ? false : undefined}
              value={editLogic.editorState.password}
            />
            <TextFieldWidget
              label={Messages.emailServer_friendlyNameFieldLabel()}
              onChange={e => editLogic.setEditorStateWithinEditMode({ friendlyName: truncateText(e.target.value) })}
              onClear={() => editLogic.setEditorStateWithinEditMode({ friendlyName: '' })}
              onKeyDown={handleKeyDown}
              testId={EDIT_FRIENDLY_NAME_FIELD_TEST_ID}
              valid={!isFriendlyNameValid(editLogic.editorState.friendlyName) ? false : undefined}
              value={editLogic.editorState.friendlyName}
            />
            <TextFieldWidget
              label={Messages.emailServer_domain()}
              onChange={e => editLogic.setEditorStateWithinEditMode({ domain: truncateText(e.target.value) })}
              onClear={() => editLogic.setEditorStateWithinEditMode({ domain: '' })}
              onKeyDown={handleKeyDown}
              required
              testId={EDIT_DOMAIN_FIELD_TEST_ID}
              valid={!isHostValid(editLogic.editorState.domain) ? false : undefined}
              value={editLogic.editorState.domain}
            />
          </div>
        </div>
        {credentialsErrorMessage && <CompactInlineAlertWidget
          children={<span>{credentialsErrorMessage}</span>}
          style={{ marginTop: 4, marginBottom: 4 }}
          type={InlineAlertType.Error}
          testId={CREDENTIALS_FORM_ERROR_TEST_ID}
        />}
        <div style={{paddingTop: 4}} className={credentialsFormButtonsClass}>
          <InlineEditorCancelButton {...editLogic} />
          <span style={{ paddingLeft: 8 }}>
            <InlineEditorSaveButton {...editLogic} />
          </span>
        </div>
      </div>
    </>
  );
}

function isStringValid(input: string): boolean {
  return !_.isUndefined(input) && input.trim().length > 0 && input.length <= MAX_INPUT_LENGTH;
}

function isHostValid(host: string): boolean {
  return !host || (isStringValid(host) && isValidDomain(host));
}

function isPortValid(port: string): boolean {
  return EditConnectionReactActionUtils.isPortValid(port);
}

function isPasswordValid(password: string): boolean {
  return !password || isStringValid(password);
}

function isFriendlyNameValid(friendlyName: string): boolean {
  return !friendlyName || isStringValid(friendlyName);
}